// CustomerReviewsMobileComponent.js
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getFeefoDetails } from '../../../api/apiServices';
import Image from '../../Image/Image';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';

const StarFullIcon =
  'https://assets.askhippo.co.za/Icons/common/Star_Full_Customer_Review.svg';
const StarEmptyIcon =
  'https://assets.askhippo.co.za/Icons/common/star_empty.svg';
const LOGO_URL = 'https://assets.askhippo.co.za/Pages/Common/FeefoLogo.svg';

const CustomerReviewsMobileComponent = ({ data }) => {
  const { title, description, footer, stars: jsonStars } = data;

  // ⭐ Header stars: API AverageScore -> rounded 0–5 (fallback to JSON stars)
  const [headerStars, setHeaderStars] = useState(
    Math.min(5, Math.max(0, Math.round(jsonStars || 0)))
  );

  // Cards: 3 random from API (placeholders until loaded)
  const [cardReviews, setCardReviews] = useState([
    { name: '—', stars: 5, text: 'Loading review…' },
    { name: '—', stars: 5, text: 'Loading review…' },
    { name: '—', stars: 5, text: 'Loading review…' },
  ]);

  useEffect(() => {
    let alive = true;

    const pickRandomThree = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      const copy = arr.slice();
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.slice(0, 3);
    };

    (async () => {
      try {
        const res = await getFeefoDetails();
        if (!alive) return;

        // Update header stars from AverageScore
        const avg = Number(res?.AverageScore) || 0;
        setHeaderStars(Math.min(5, Math.max(0, Math.round(avg))));

        // Map 3 random reviews for cards
        const chosen = pickRandomThree(res?.LatestReviews || []).map((r) => ({
          name: r?.Customer || 'Anonymous',
          stars: Math.min(5, Math.max(0, Number(r?.Rating) || 0)),
          text: (r?.ReviewText || '').trim(),
        }));

        while (chosen.length < 3) {
          chosen.push({ name: 'Anonymous', stars: 5, text: 'Great service!' });
        }

        setCardReviews(chosen);
      } catch {
        // Fallbacks
        setHeaderStars(Math.min(5, Math.max(0, Math.round(jsonStars || 0))));
        setCardReviews([
          { name: 'Anonymous', stars: 5, text: 'Great service!' },
          { name: 'Anonymous', stars: 5, text: 'Quick and easy.' },
          { name: 'Anonymous', stars: 5, text: 'Highly recommended.' },
        ]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [jsonStars]);

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Blue Section */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#162F3B',
          borderRadius: '48px',
          padding: '32px 16px 24px 16px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        {/* Header Stars (from API averageScore) */}
        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            mt: '24px',
            width: '100%',
            px: '16px',
            boxSizing: 'border-box',
            justifyContent: 'center',
          }}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <ScrollFadeWrapper
              key={i}
              delay={(i - 1) * 0.1}
              observerOptions={{ rootMargin: '0px 0px -10% 0px' }}
              twist={-36}
            >
              <img
                src={i <= headerStars ? StarFullIcon : StarEmptyIcon}
                alt="star"
                width={55}
                height={54}
                style={{
                  width: 'clamp(42px, 12vw, 55px)',
                  height: 'auto',
                  aspectRatio: '55 / 54',
                  display: 'block',
                }}
              />
            </ScrollFadeWrapper>
          ))}
        </Box>

        {/* Title & Description (from JSON) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2rem',
              lineHeight: '2.25rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#FFFFFF',
            }}
          >
            {title}
          </Typography>

          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.25rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#FFFFFF',
              px: '16px',
              boxSizing: 'border-box',
              alignSelf: 'stretch',
              whiteSpace: 'pre-line',
            }}
          >
            {String(description || '')
              .split(/(\*\*[^*]+\*\*)/g)
              .map((part, i) => {
                if (/^\*\*[^*]+\*\*$/.test(part)) {
                  return (
                    <strong key={i} style={{ fontWeight: 700 }}>
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return part;
              })}
          </Typography>
        </Box>

        {/* Review Cards (3 random from API) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            width: 'calc(100% + 32px)',
            mx: '-16px',
            pl: '9%',
            pr: '16px',
            alignItems: 'stretch',
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: '8px',
            scrollSnapType: 'x mandatory',
            scrollPaddingInlineStart: '9%',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {cardReviews.map((review, i) => (
            <ScrollFadeWrapper
              key={i}
              delay={i * 0.15}
              duration={0.7}
              sx={{
                flex: '0 0 auto',
                width: 'clamp(220px, 70vw, 320px)',
                minWidth: 0,
                maxWidth: '320px',
                display: 'flex',
                scrollSnapAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '280px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '32px',
                  padding: '24px',
                  boxShadow: '0px 6px 16px #162F3B1F, 0px 1px 2px #A2ACB11F',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  boxSizing: 'border-box',
                }}
              >
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  {[...Array(5)].map((_, index) => (
                    <img
                      key={index}
                      src={index < review.stars ? StarFullIcon : StarEmptyIcon}
                      alt="star"
                      width={25}
                      height={24}
                      style={{ width: '25px', height: '24px' }}
                    />
                  ))}
                </Box>

                <Typography
                  component={'p'}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                    textAlign: 'left',
                    whiteSpace: 'pre-line',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 4,
                    alignSelf: 'stretch',
                  }}
                >
                  {String(review.text || '')
                    .split(/(\*\*[^*]+\*\*)/g)
                    .map((part, j) => {
                      if (/^\*\*[^*]+\*\*$/.test(part)) {
                        return (
                          <strong key={j} style={{ fontWeight: 700 }}>
                            {part.slice(2, -2)}
                          </strong>
                        );
                      }
                      return part;
                    })}
                </Typography>

                <Typography
                  component={'p'}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                    color: '#162F3B',
                    textAlign: 'center',
                    mt: 'auto',
                  }}
                >
                  {review.name}
                </Typography>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>

        {/* Logo (Cloudflare R2) */}
        <Image
          src={LOGO_URL}
          alt="Company Logo"
          width={172}
          height={40}
          sx={{ width: '172px', height: 'auto', mt: '8px', mb: '4px' }}
        />

        {/* Footer (from JSON) */}
        <Typography
          component={'p'}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#FFFFFF',
            textAlign: 'center',
            px: '16px',
            boxSizing: 'border-box',
            alignSelf: 'stretch',
            mt: '0px',
            whiteSpace: 'pre-line',
          }}
        >
          {footer}
        </Typography>
      </Box>
    </ScrollFadeSection>
  );
};

export default CustomerReviewsMobileComponent;
